// 公告 / 新闻管理屏。type='news' 仅在 /news 列表展示；type='announcement'
// 额外支持弹窗（popupMode），客户端 LoginModalHost 旁边的 AnnouncementPopupHost
// 接住。
//
// 表单关键字段：
//  - type 决定走"新闻"还是"公告"列表
//  - popupMode（仅 announcement）：none/always/daily/once
//  - startsAt / endsAt 决定可见窗口（endsAt 留空 = 永久）
//  - active 总开关
//
// 删除是硬删除（物理 DELETE），与 dict 的软删（active=0）不同 — 删过期公告
// 通常没有保留意义。

import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import {
  api, errorMessage,
  type AnnouncementRow, type AnnouncementType, type AnnouncementPopupMode, type AnnouncementColor,
  type AnnouncementCreateInput, type AnnouncementPatchInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useI18n } from '@/lib/i18n'
import { PageError, PageLoading } from './Stats'

const TYPES: AnnouncementType[] = ['news', 'announcement']
const POPUP_MODES: AnnouncementPopupMode[] = ['none', 'always', 'daily', 'once']
const COLORS: AnnouncementColor[] = ['brand', 'info', 'success', 'warn', 'danger']

const POPUP_LABEL: Record<AnnouncementPopupMode, { zh: string; en: string }> = {
  none:   { zh: '不弹窗',         en: 'No popup' },
  always: { zh: '每次打开都弹',   en: 'Every visit' },
  daily:  { zh: '每天最多一次',   en: 'Daily' },
  once:   { zh: '永久仅弹一次',   en: 'Once ever' },
}

interface EditingForm {
  id: string | null  // null = 新建
  type: AnnouncementType
  tag: string
  colorVariant: AnnouncementColor
  titleZh: string
  titleEn: string
  bodyZh: string
  bodyEn: string
  popupMode: AnnouncementPopupMode
  startsAt: string  // datetime-local 字符串
  endsAt: string    // 空 = 永久
  active: 0 | 1
}

function emptyForm(): EditingForm {
  // 默认 startsAt = 现在；endsAt 留空（永久）
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const dt = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
  return {
    id: null, type: 'news', tag: 'ANN', colorVariant: 'brand',
    titleZh: '', titleEn: '', bodyZh: '', bodyEn: '',
    popupMode: 'none', startsAt: dt, endsAt: '',
    active: 1,
  }
}

function rowToForm(r: AnnouncementRow): EditingForm {
  const fmt = (sec: number) => {
    const d = new Date(sec * 1000)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
  return {
    id: r.id,
    type: r.type, tag: r.tag, colorVariant: r.colorVariant,
    titleZh: r.titleZh, titleEn: r.titleEn ?? '',
    bodyZh: r.bodyZh, bodyEn: r.bodyEn ?? '',
    popupMode: r.popupMode,
    startsAt: fmt(r.startsAt),
    endsAt: r.endsAt ? fmt(r.endsAt) : '',
    active: r.active === 1 ? 1 : 0,
  }
}

function formToInput(f: EditingForm): AnnouncementCreateInput {
  const startsAt = Math.floor(new Date(f.startsAt).getTime() / 1000)
  const endsAt = f.endsAt ? Math.floor(new Date(f.endsAt).getTime() / 1000) : null
  return {
    type: f.type, tag: f.tag.trim(), colorVariant: f.colorVariant,
    titleZh: f.titleZh.trim(), titleEn: f.titleEn.trim() || null,
    bodyZh: f.bodyZh, bodyEn: f.bodyEn || null,
    popupMode: f.popupMode,
    startsAt, endsAt,
    active: f.active,
  }
}

function fmtDate(sec: number | null): string {
  if (!sec) return '—'
  const d = new Date(sec * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth()+1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function AnnouncementsScreen() {
  const toast = useToast()
  const { lang } = useI18n()
  const isZh = lang === 'zh'
  const [filter, setFilter] = useState<AnnouncementType | 'all'>('all')
  const [rows, setRows] = useState<AnnouncementRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<EditingForm | null>(null)
  const [deleting, setDeleting] = useState<AnnouncementRow | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  function load() {
    setBusy(true); setError(null)
    api.listAnnouncements(filter === 'all' ? undefined : filter)
      .then((r) => setRows(r.items))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }
  useEffect(() => { load() /* eslint-disable-next-line */ }, [filter])

  async function handleSave() {
    if (!editing) return
    if (!editing.titleZh.trim() || !editing.bodyZh.trim()) {
      toast.error(isZh ? '中文标题与正文必填' : 'Chinese title and body are required')
      return
    }
    setBusy(true)
    try {
      const input = formToInput(editing)
      if (editing.id) {
        await api.patchAnnouncement(editing.id, input as AnnouncementPatchInput)
        toast.success(isZh ? '已保存' : 'Saved')
      } else {
        await api.createAnnouncement(input)
        toast.success(isZh ? '已创建' : 'Created')
      }
      setEditing(null)
      load()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await api.deleteAnnouncement(deleting.id)
      toast.success(isZh ? '已删除' : 'Deleted')
      setDeleting(null)
      load()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setDeleteBusy(false)
    }
  }

  if (rows === null && busy) return <PageLoading/>
  if (error) return <PageError msg={error}/>

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">{isZh ? '公告 / 新闻' : 'News & Announcements'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isZh
              ? '新闻：仅在 /news 列表展示。公告：可弹窗（每天/每次/仅一次），同样在列表置顶。'
              : 'News: shown in /news only. Announcements: support popup (daily/always/once) and pin to the top of the list.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="bg-card border border-border rounded-md text-sm px-3 py-1.5"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <option value="all">{isZh ? '全部' : 'All'}</option>
            <option value="news">{isZh ? '新闻' : 'News'}</option>
            <option value="announcement">{isZh ? '公告' : 'Announcement'}</option>
          </select>
          <Button onClick={() => setEditing(emptyForm())}>
            <Plus className="h-4 w-4"/>
            {isZh ? '新建' : 'New'}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>{isZh ? '类型' : 'Type'}</Th>
              <Th>Tag</Th>
              <Th>{isZh ? '标题' : 'Title'}</Th>
              <Th>{isZh ? '弹窗' : 'Popup'}</Th>
              <Th>{isZh ? '起止' : 'Window'}</Th>
              <Th>{isZh ? '状态' : 'State'}</Th>
              <Th></Th>
            </Tr>
          </THead>
          <TBody>
            {(rows || []).map((r) => (
              <Tr key={r.id}>
                <Td>
                  <Badge tone={r.type === 'announcement' ? 'warning' : 'default'}>
                    {r.type === 'announcement' ? (isZh ? '公告' : 'Announcement') : (isZh ? '新闻' : 'News')}
                  </Badge>
                </Td>
                <Td><Badge tone={r.colorVariant === 'success' ? 'success' : r.colorVariant === 'warn' ? 'warning' : r.colorVariant === 'danger' ? 'danger' : 'default'}>{r.tag}</Badge></Td>
                <Td className="font-medium">{r.titleZh}</Td>
                <Td className="text-xs text-muted-foreground">
                  {POPUP_LABEL[r.popupMode][isZh ? 'zh' : 'en']}
                </Td>
                <Td className="text-xs text-muted-foreground tabular-nums">
                  {fmtDate(r.startsAt)} → {r.endsAt ? fmtDate(r.endsAt) : (isZh ? '永久' : 'forever')}
                </Td>
                <Td>
                  <Badge tone={r.active ? 'success' : 'default'}>{r.active ? (isZh ? '启用' : 'Active') : (isZh ? '停用' : 'Inactive')}</Badge>
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(rowToForm(r))}>{isZh ? '编辑' : 'Edit'}</Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(r)}>{isZh ? '删除' : 'Delete'}</Button>
                </Td>
              </Tr>
            ))}
            {rows && rows.length === 0 && (
              <Tr><Td colSpan={7} className="text-center text-muted-foreground py-8">{isZh ? '暂无内容' : 'No items'}</Td></Tr>
            )}
          </TBody>
        </Table>
      </div>

      {editing && (
        <EditDrawer
          form={editing}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          busy={busy}
          isZh={isZh}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={isZh ? '删除公告？' : 'Delete announcement?'}
        message={deleting ? `${isZh ? '将删除' : 'About to delete'}: 「${deleting.titleZh}」` : ''}
        confirmLabel={isZh ? '删除' : 'Delete'}
        cancelLabel={isZh ? '取消' : 'Cancel'}
        tone="danger"
        busy={deleteBusy}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}

interface EditDrawerProps {
  form: EditingForm
  onChange: (f: EditingForm) => void
  onClose: () => void
  onSave: () => void
  busy: boolean
  isZh: boolean
}
function EditDrawer({ form, onChange, onClose, onSave, busy, isZh }: EditDrawerProps) {
  const update = <K extends keyof EditingForm>(k: K, v: EditingForm[K]) => onChange({ ...form, [k]: v })

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-semibold">{form.id ? (isZh ? '编辑' : 'Edit') : (isZh ? '新建' : 'New')}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4"/></Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={isZh ? '类型' : 'Type'}>
              <select className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full" value={form.type} onChange={(e) => update('type', e.target.value as AnnouncementType)}>
                <option value="news">{isZh ? '新闻' : 'News'}</option>
                <option value="announcement">{isZh ? '公告' : 'Announcement'}</option>
              </select>
            </Field>
            <Field label="Tag">
              <Input value={form.tag} onChange={(e) => update('tag', e.target.value)} placeholder="ANN / FIX / TUT / LOG"/>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={isZh ? '色调' : 'Color variant'}>
              <select className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full" value={form.colorVariant} onChange={(e) => update('colorVariant', e.target.value as AnnouncementColor)}>
                {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={isZh ? '弹窗模式' : 'Popup mode'}>
              <select
                className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full"
                value={form.popupMode}
                onChange={(e) => update('popupMode', e.target.value as AnnouncementPopupMode)}
              >
                {POPUP_MODES.map((m) => <option key={m} value={m}>{POPUP_LABEL[m][isZh ? 'zh' : 'en']}</option>)}
              </select>
              {form.type === 'news' && form.popupMode !== 'none' && (
                <div className="mt-1 text-[11px] text-warn">
                  {isZh
                    ? '注意：「新闻」不会弹窗，仅「公告」类型 + popupMode≠不弹窗 才会触发客户端弹窗。'
                    : 'Heads-up: "News" never pops up. Only "Announcement" + popupMode!=none triggers client popup.'}
                </div>
              )}
            </Field>
          </div>

          <Field label={isZh ? '中文标题 *' : 'Title (zh) *'}>
            <Input value={form.titleZh} onChange={(e) => update('titleZh', e.target.value)} maxLength={200}/>
          </Field>
          <Field label={isZh ? '英文标题（选填）' : 'Title (en, optional)'}>
            <Input value={form.titleEn} onChange={(e) => update('titleEn', e.target.value)} maxLength={200}/>
          </Field>

          <Field label={isZh ? '中文正文 *（支持换行）' : 'Body (zh) *'}>
            <textarea
              className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full font-mono"
              rows={5}
              value={form.bodyZh}
              onChange={(e) => update('bodyZh', e.target.value)}
            />
          </Field>
          <Field label={isZh ? '英文正文（选填）' : 'Body (en, optional)'}>
            <textarea
              className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full font-mono"
              rows={4}
              value={form.bodyEn}
              onChange={(e) => update('bodyEn', e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={isZh ? '生效时间' : 'Starts at'}>
              <Input type="datetime-local" value={form.startsAt} onChange={(e) => update('startsAt', e.target.value)}/>
            </Field>
            <Field label={isZh ? '结束时间（留空 = 永久）' : 'Ends at (blank = forever)'}>
              <Input type="datetime-local" value={form.endsAt} onChange={(e) => update('endsAt', e.target.value)}/>
            </Field>
          </div>

          <Field label={isZh ? '状态' : 'State'}>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active === 1} onChange={(e) => update('active', e.target.checked ? 1 : 0)}/>
              {isZh ? '启用（关闭则不返公开 API）' : 'Active (off = excluded from public API)'}
            </label>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={busy}>{isZh ? '取消' : 'Cancel'}</Button>
          <Button onClick={onSave} disabled={busy}>{busy ? '...' : (isZh ? '保存' : 'Save')}</Button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
